// Single source of truth for platform tier gating on the frontend.
// Mirror of Backend_Web/api/tiers.py — keep the two in sync (both are small).
//
// Tiers: basic < pro < plus < advisor. `advisor` inherits every consumer
// feature ("Everything in Plus"), so it ranks highest. Tier switching is free
// until Stripe lands; these constants only drive what the UI shows/locks.

export const TIER_RANK = {
  basic: 0,
  pro: 1,
  plus: 2,
  advisor: 3,
};

// Display label for a tier key.
export const TIER_LABEL = {
  basic: "Basic",
  pro: "Pro",
  plus: "Plus",
  advisor: "Advisor",
};

// Launch price anchors (display-only) used in upgrade copy.
export const TIER_PRICE_HINT = {
  pro: "Free during launch · normally $12/mo",
  plus: "Free during launch · normally $29/mo",
};

/** True when `current` tier is at least `min` tier. */
export const hasTier = (current, min) =>
  (TIER_RANK[current] ?? 0) >= (TIER_RANK[min] ?? 0);

/**
 * Feature registry. Each feature names the minimum tier required and the copy
 * used by the upgrade modal. `bullets` are the headline perks of the target
 * tier (also pulled live from the plan catalog, but kept here as a fallback).
 */
export const FEATURES = {
  performance: {
    minTier: "pro",
    label: "Performance analytics",
    modalCopy: {
      title: "Unlock deeper performance analytics",
      body: "Benchmark vs. the S&P 500, track drawdowns, and see time-weighted returns across every account.",
    },
  },
  recommendations: {
    minTier: "pro",
    label: "AI recommendations",
    modalCopy: {
      title: "Get AI recommendations with Pro",
      body: "Personalized rebalancing and optimization ideas tuned to what you actually hold.",
    },
  },
  tax_harvest: {
    minTier: "pro",
    label: "Tax-loss harvesting",
    modalCopy: {
      title: "Tax-loss harvesting is a Pro feature",
      body: "See exactly which lots to sell to offset gains and trim your tax bill — refreshed daily.",
    },
  },
  unlimited_accounts: {
    minTier: "pro",
    label: "Unlimited accounts",
    modalCopy: {
      title: "Add unlimited accounts with Pro",
      body: "Basic includes 1 linked brokerage and 5 manual assets. Go Pro to track everything you own.",
    },
  },
  data_export: {
    minTier: "pro",
    label: "Data export",
    modalCopy: {
      title: "Export your data with Pro",
      body: "Download your full portfolio and history as CSV anytime.",
    },
  },
  public_share: {
    minTier: "pro",
    label: "Public portfolio share",
    modalCopy: {
      title: "Share your portfolio with Pro",
      body: "Publish a clean, shareable public link to your portfolio.",
    },
  },
  advisor_subscribe: {
    minTier: "pro",
    label: "Advisor subscriptions",
    modalCopy: {
      title: "Subscribe to advisors with Pro",
      body: "Pro unlocks up to 3 advisor subscriptions; Plus makes them unlimited.",
    },
  },
  council_unlimited: {
    minTier: "plus",
    label: "Unlimited AI Council",
    modalCopy: {
      title: "Go unlimited with Plus",
      body: "Unlimited AI chat and the full multi-agent Council weighing in on every question.",
    },
  },
  ai_daily_limit: {
    minTier: "pro",
    label: "More AI messages",
    modalCopy: {
      title: "You've hit today's message limit",
      body: "Basic includes 10 AI messages a day. Upgrade to Pro for 100/day, or Plus for unlimited.",
    },
  },
};

/** Min tier required for a feature key (defaults to basic/open). */
export const featureMinTier = (key) => FEATURES[key]?.minTier ?? "basic";

/** True when the current tier can access the given feature key. */
export const meetsFeature = (currentTier, key) =>
  hasTier(currentTier || "basic", featureMinTier(key));

// Per-day AI chat message caps by tier. null = unlimited.
export const AI_DAILY_LIMIT = {
  basic: 10,
  pro: 100,
  plus: null,
  advisor: null,
};
